import { useEffect, useRef, useState } from 'react';
import { useForceUpdate } from '@djs/react';
import { useDelayValue } from '@dui/hooks';
import { MediaPlayerController } from '../MediaPlayerController/MediaPlayerController';
import { formatTime } from './helpers';

interface UseVoiceMessageParams {
  mediaPlayerController?: MediaPlayerController;
  url: string;
  mediaId: string;
  duration: number;
  commonSpeedControl: boolean;
}

export function useVoiceMessage(params: UseVoiceMessageParams) {
  const { mediaId, url, mediaPlayerController: mediaPlayerControllerParam, duration, commonSpeedControl } = params;

  const [mediaPlayerController] = useState(() => mediaPlayerControllerParam || new MediaPlayerController());

  const audioPlayerController =
    mediaPlayerController.activeMedia?.id === mediaId ? mediaPlayerController.activeMedia.item : void 0;

  const audioStatus = audioPlayerController?.status || ('idle' as const);

  const forceUpdate = useForceUpdate();

  const speed = mediaPlayerController.speed;
  const getProgressSetterRef = useRef<(percent: number) => void>(null);

  const timerRef = useRef<HTMLSpanElement>(null);

  const isTogglePlayingDisabled = audioStatus === 'destroyed';
  const isWaveformDisabled =
    isTogglePlayingDisabled || audioStatus === 'idle' || audioStatus === 'failed' || audioStatus === 'loading';

  const togglePlaying = () => {
    if (audioPlayerController) {
      audioPlayerController.togglePlaying();
    } else {
      mediaPlayerController.setAudioMedia(mediaId, url, commonSpeedControl);
    }
  };

  const onProgressMoveStart = () => {
    audioPlayerController?.pause();
  };

  const onProgressMove = (ratio: number) => {
    if (audioPlayerController) {
      audioPlayerController.currentTime = ratio * audioPlayerController.duration;

      if (timerRef.current) {
        timerRef.current.textContent = formatTime(audioPlayerController.currentTime);
      }

      getProgressSetterRef.current?.(audioPlayerController.currentTime / audioPlayerController.duration);
    }
  };

  const onProgressMoveEnd = (ratio: number) => {
    onProgressMove(ratio);
    if (ratio < 1) {
      audioPlayerController?.play();
    }
  };

  /**
   * Реактивность состояния
   */
  useEffect(() => {
    if (!audioPlayerController) {
      return;
    }

    audioPlayerController.on('statusChanged', forceUpdate);

    return () => {
      audioPlayerController.off('statusChanged', forceUpdate);
    };
  }, [audioPlayerController, forceUpdate]);

  useEffect(() => {
    mediaPlayerController.on('mediaChanged', forceUpdate);
    mediaPlayerController.on('speedChanged', forceUpdate);

    return () => {
      mediaPlayerController.off('mediaChanged', forceUpdate);
      mediaPlayerController.off('speedChanged', forceUpdate);
    };
  }, [audioPlayerController, mediaPlayerController, forceUpdate]);

  const isPlaying = audioStatus === 'playing';

  useEffect(() => {
    if (!audioPlayerController) {
      if (timerRef.current) {
        timerRef.current.textContent = formatTime(duration);
      }

      getProgressSetterRef.current?.(1);

      return;
    }

    if (!isPlaying) {
      if (timerRef.current) {
        timerRef.current.textContent = formatTime(audioPlayerController.currentTime);
      }

      getProgressSetterRef.current?.(audioPlayerController.currentTime / audioPlayerController.duration);

      return;
    }

    let animationId = requestAnimationFrame(function animation() {
      if (timerRef.current) {
        timerRef.current.textContent = formatTime(audioPlayerController.currentTime);
      }

      getProgressSetterRef.current?.(audioPlayerController.currentTime / audioPlayerController.duration);

      animationId = requestAnimationFrame(animation);
    });

    return () => cancelAnimationFrame(animationId);
  }, [audioPlayerController, isPlaying, duration]);

  const onSpeedClick = () => mediaPlayerController.switchSpeed();

  useEffect(() => {
    return () => {
      mediaPlayerController.resetMedia(mediaId);
    };
  }, [mediaPlayerController, mediaId]);

  return {
    togglePlaying,
    // Через useDelayValue исключаем визуальную быструю смену стейта (click->loading->playing)
    audioStatus: useDelayValue(audioStatus, [audioStatus], audioStatus === 'loading' ? 200 : 0),
    isWaveformDisabled,
    isTogglePlayingDisabled,
    timerRef,
    getProgressSetterRef,
    onProgressMoveStart,
    onProgressMove,
    onProgressMoveEnd,
    speed,
    onSpeedClick,
  };
}
